import {
  ColumnType,
  type Condition,
  ConditionType,
  DefaultOperators,
  type EventCondition,
  initialEventCondition,
  initialJourneySetCondition,
  initialNumberOfCondition,
  initialPropertyCondition,
  initialRealtimeEventWindow,
  initialSetCondition,
  type JourneyEventColumn,
  type JourneySetCondition,
  type NumberOfCondition,
  type PropertyCondition,
  type SegmentSetCondition,
  TraitType,
} from "src/types/visual";
import type { FilterColumnOption } from "./types";
import { FilterOption } from "./constants";

export type ConditionBuilder = (column: FilterColumnOption) => Condition;

export const buildConditionFromColumn: ConditionBuilder = (
  column: FilterColumnOption,
):
  | PropertyCondition
  | NumberOfCondition
  | EventCondition
  | SegmentSetCondition
  | JourneySetCondition => {
  switch (column.type) {
    case FilterOption.Property:
    case FilterOption.Trait:
    case FilterOption.TraitTemplate:
    case FilterOption.JourneyMemberState:
    case FilterOption.JourneyVariable:
      return {
        ...initialPropertyCondition,
        id: crypto.randomUUID(),
        type: ConditionType.Property,
        propertyType: column.propertyType ?? null,
        property: column.value,
        propertyOptions: {
          traitType:
            column.type === FilterOption.TraitTemplate
              ? "trait_template"
              : column.type === FilterOption.Trait
                ? "trait"
                : undefined,
        },
        operator: column.propertyType
          ? DefaultOperators[column.propertyType]
          : null,
        value: null,
      };
    case FilterOption.EventProperty:
      return {
        ...initialPropertyCondition,
        id: crypto.randomUUID(),
        type: ConditionType.Property,
        propertyType: null,
        property: {
          type: "related",
          path: [],
          column: {
            type: "journey_event",
            modelId: column.value.modelId,
            name: "", // User will select this in the PropertyFilter
          } satisfies JourneyEventColumn,
        },
        propertyOptions: {},
        operator: null,
        value: null,
      };
    case FilterOption.InlineTrait:
      return {
        type: ConditionType.Property,
        id: crypto.randomUUID(),
        property: {
          type: "related",
          column: {
            type: "inline_trait",
            traitType: TraitType.Average,
            traitConfig: {},
            conditions: [],
            relationshipId: "",
          },
          path: [],
        },
        propertyType: ColumnType.Unknown,
        propertyOptions: {
          traitType: "inline_trait",
        },
        operator: null,
        value: null,
      };
    case FilterOption.Relation:
      return {
        ...initialNumberOfCondition,
        id: crypto.randomUUID(),
        relationshipId: column.value.relationshipId,
      };
    case FilterOption.Event:
    case FilterOption.RealtimeEvent:
      return {
        ...initialEventCondition,
        id: crypto.randomUUID(),
        relationshipId: column.value.relationshipId,
        eventModelId: column.value.eventModelId,
        window:
          column.type === FilterOption.RealtimeEvent
            ? initialRealtimeEventWindow
            : undefined,
      };
    case FilterOption.MessageEvent:
      return {
        ...initialEventCondition,
        id: crypto.randomUUID(),
        relationshipId: column.value.relationshipId,
        eventModelId: column.value.eventModelId,
        funnelCondition: column.value.funnelCondition,
      };
    case FilterOption.Audience:
      return {
        ...initialSetCondition,
        id: crypto.randomUUID(),
        modelId: column.value.modelId,
      };
    case FilterOption.Journey:
      return {
        ...initialJourneySetCondition,
        id: crypto.randomUUID(),
        journeyIds: [column.value.journeyId],
      };
  }
};
